import { geoMercator, geoPath, json, select } from 'd3';

export default class MetaMap {
  constructor(element, config) {
    this.element = typeof element === 'string' ? document.querySelector(element) : element;
    this.config = config;
    this.selectedCountries = new Set(config.selectedCountries || []);
    this.width = config.width || 802;
    this.height = config.height || 530;

    if (!this.element) throw new Error('Map container is missing');

    this.svg = select(this.element).append('svg')
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('aria-hidden', this.config.onCountryClick ? null : 'true')
      .attr('role', this.config.onCountryClick ? 'group' : null)
      .attr('aria-label', this.config.onCountryClick ? 'Country selection map' : null);
    this.ready = this.render();
  }

  async render() {
    const data = await json(this.config.mapPath);
    if (!data || !Array.isArray(data.features)) throw new Error('Invalid world map data');

    this.features = data.features;
    this.featuresByCode = new Map();
    this.features.forEach((feature) => {
      const code = feature.properties?.id;
      if (!/^[A-Z]{2}$/.test(code || '')) return;
      this.featuresByCode.set(code, [...(this.featuresByCode.get(code) || []), feature]);
    });
    this.projection = geoMercator().fitExtent(
      [[8, 8], [this.width - 8, this.height - 8]],
      data,
    );
    this.path = geoPath(this.projection);
    this.paths = this.svg.append('g').selectAll('path')
      .data(this.features)
      .join('path')
      .attr('class', 'meta-map__country')
      .attr('data-country-code', (feature) => feature.properties.id)
      .attr('d', this.path)
      .attr('tabindex', (feature) => this.config.onCountryClick && this.featuresByCode.has(feature.properties.id) ? 0 : null)
      .attr('role', (feature) => this.config.onCountryClick && this.featuresByCode.has(feature.properties.id) ? 'button' : null)
      .attr('aria-label', (feature) => this.config.countryNames?.[feature.properties.id] || feature.properties.id);

    if (this.config.onCountryClick) {
      this.paths.on('click', (event, feature) => {
        if (this.featuresByCode.has(feature.properties.id)) this.config.onCountryClick(feature.properties.id);
      });
      this.paths.on('keydown', (event, feature) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (this.featuresByCode.has(feature.properties.id)) this.config.onCountryClick(feature.properties.id);
        }
      });
    }

    this.selectCountryList([...this.selectedCountries]);
    return this;
  }

  selectCountryList(codes) {
    this.selectedCountries = new Set(codes);
    this.paths?.classed('is-selected', (feature) => this.selectedCountries.has(feature.properties.id));
  }

  countryAnchor(code) {
    const features = this.featuresByCode?.get(code);
    if (!features || !this.path) return null;

    let largestPolygon = null;
    let maxArea = -1;

    for (const feature of features) {
      const geom = feature.geometry;
      if (!geom) continue;

      if (geom.type === 'Polygon') {
        const pGeom = { type: 'Polygon', coordinates: geom.coordinates };
        const area = this.path.area(pGeom);
        if (area > maxArea) {
          maxArea = area;
          largestPolygon = pGeom;
        }
      } else if (geom.type === 'MultiPolygon') {
        for (const coords of geom.coordinates) {
          const pGeom = { type: 'Polygon', coordinates: coords };
          const area = this.path.area(pGeom);
          if (area > maxArea) {
            maxArea = area;
            largestPolygon = pGeom;
          }
        }
      }
    }

    const target = largestPolygon || { type: 'FeatureCollection', features };
    const [x, y] = this.path.centroid(target);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return {
      x_percent: Math.round((x / this.width) * 10000) / 100,
      y_percent: Math.round((y / this.height) * 10000) / 100,
    };
  }
}
